import { Skeleton } from "@/components/ui/skeleton";
import { useLang } from "@/contexts/LangContext";
import { legacyAppPath } from "@/const/appRoutes";

const routeProfiles = [
  [/^\/studio(?:\/|$)/, "studio"],
  [/^\/docs(?:\/|$)/, "docs"],
  [/^\/models(?:\/|$)/, "models"],
  [/^\/(?:billing|statistics)(?:\/|$)/, "usage"],
  [/^\/legal(?:\/|$)/, "legal"],
  [/^\/home(?:\/|$)/, "home"],
  [/^\/api-keys(?:\/|$)/, "api"],
  [/^\/guides\/[^/]+/, "guide-detail"],
  [/^\/guides(?:\/|$)/, "guides"],
  [/^\/referrals(?:\/|$)/, "referrals"],
  [/^\/support(?:\/|$)/, "support"],
  [/^\/functions(?:\/|$)/, "functions"],
  [/^\/profile(?:\/|$)/, "profile"],
  [/^\/pricing(?:\/|$)/, "pricing"],
  [/^\/openai-compatible-api(?:\/|$)/, "openai"],
  [/^\/anthropic-compatible-api(?:\/|$)/, "anthropic"],
  [/^\/compare-direct-api(?:\/|$)/, "compare"],
  [/^\/landing$/, "landing"],
  [/^\/$/, "landing"],
];

export function getRouteLoadingProfile(pathname = "/") {
  const canonicalPath = legacyAppPath(pathname) || pathname;
  return routeProfiles.find(([pattern]) => pattern.test(canonicalPath))?.[1] ?? "not-found";
}

function SkeletonCard({ children, className = "" }) {
  return <div className={`route-loading-card ${className}`.trim()}>{children}</div>;
}

function Heading({ subtitle = false }) {
  return (
    <div className="route-loading-heading">
      <Skeleton className="h-7 w-48 max-w-[70%]" />
      {subtitle ? <Skeleton className="h-3.5 w-72 max-w-full" /> : null}
    </div>
  );
}

function MetricCards({ count = 4 }) {
  return (
    <div className="route-loading-metrics">
      {Array.from({ length: count }, (_, index) => (
        <SkeletonCard className="route-loading-metric" key={index}>
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-28 max-w-full" />
          <Skeleton className="h-3 w-24 max-w-full" />
        </SkeletonCard>
      ))}
    </div>
  );
}

function DataRows({ rows = 4 }) {
  return (
    <div className="route-loading-rows">
      {Array.from({ length: rows }, (_, index) => (
        <div className="route-loading-row" key={index}>
          <Skeleton className="h-8 w-8 rounded-xl" />
          <div className="route-loading-row-copy">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-2.5 w-1/3" />
          </div>
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

function ArticleLines({ count = 7 }) {
  return (
    <div className="route-loading-article">
      <Skeleton className="h-4 w-2/3" />
      {Array.from({ length: count }, (_, index) => (
        <Skeleton className="h-3.5 w-full" key={index} />
      ))}
      <Skeleton className="mt-3 h-4 w-1/2" />
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton className="h-3.5 w-full" key={`tail-${index}`} />
      ))}
    </div>
  );
}

function DashboardLoading() {
  return (
    <>
      <div className="route-loading-welcome">
        <Skeleton className="h-3.5 w-40" />
        <Skeleton className="h-6 w-64 max-w-full" />
      </div>
      <SkeletonCard className="route-loading-cta">
        <Skeleton className="h-10 w-10 rounded-2xl" />
        <div className="route-loading-row-copy">
          <Skeleton className="h-4 w-44 max-w-full" />
          <Skeleton className="h-3 w-60 max-w-full" />
        </div>
        <Skeleton className="h-8 w-24" />
      </SkeletonCard>
      <MetricCards />
      <SkeletonCard className="route-loading-chart">
        <Skeleton className="h-4 w-40" />
        <div className="route-loading-chart-bars" aria-hidden="true">
          {[35, 54, 43, 76, 62, 88, 52, 70, 46, 80, 57, 68].map((height, index) => (
            <Skeleton className="route-loading-chart-bar" style={{ height: `${height}%` }} key={index} />
          ))}
        </div>
      </SkeletonCard>
      <SkeletonCard className="route-loading-panel">
        <Skeleton className="h-4 w-48" />
        <DataRows rows={3} />
      </SkeletonCard>
    </>
  );
}

function DocsLoading() {
  return (
    <div className="route-loading-docs">
      <div className="route-loading-docs-heading">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-8 w-72 max-w-full" />
        <Skeleton className="h-3.5 w-96 max-w-full" />
      </div>
      <div className="route-loading-docs-layout">
        <SkeletonCard className="route-loading-docs-nav">
          <Skeleton className="h-9 w-full rounded-xl" />
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton className="h-3.5 w-full" key={index} />
          ))}
        </SkeletonCard>
        <SkeletonCard className="route-loading-docs-content">
          <ArticleLines count={8} />
        </SkeletonCard>
      </div>
    </div>
  );
}

function CatalogLoading() {
  return (
    <>
      <Heading />
      <SkeletonCard className="route-loading-toolbar">
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 flex-1" />
        <Skeleton className="h-9 w-24" />
      </SkeletonCard>
      <div className="route-loading-models">
        {Array.from({ length: 6 }, (_, index) => (
          <SkeletonCard className="route-loading-model" key={index}>
            <Skeleton className="h-9 w-9 rounded-xl" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="mt-auto h-3 w-1/2" />
          </SkeletonCard>
        ))}
      </div>
    </>
  );
}

function DetailLoading({ code = false }) {
  return (
    <>
      <Heading subtitle />
      <SkeletonCard className="route-loading-detail">
        <ArticleLines count={code ? 4 : 7} />
        {code ? (
          <div className="route-loading-code">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton className="h-3.5 w-full" key={index} />
            ))}
          </div>
        ) : null}
      </SkeletonCard>
    </>
  );
}

function RouteLoadingContent({ profile }) {
  switch (profile) {
    case "landing":
      return (
        <>
          <div className="route-loading-landing-hero">
            <Skeleton className="h-3 w-36" />
            <Skeleton className="h-10 w-[min(620px,90%)]" />
            <Skeleton className="h-4 w-[min(480px,78%)]" />
            <Skeleton className="mt-2 h-11 w-40 rounded-full" />
          </div>
          <SkeletonCard className="route-loading-marquee">
            {Array.from({ length: 7 }, (_, index) => (
              <Skeleton className="h-8 w-24 rounded-full" key={index} />
            ))}
          </SkeletonCard>
          <div className="route-loading-landing-grid">
            {Array.from({ length: 3 }, (_, index) => (
              <SkeletonCard key={index}><ArticleLines count={2} /></SkeletonCard>
            ))}
          </div>
        </>
      );
    case "home":
      return <DashboardLoading />;
    case "docs":
      return <DocsLoading />;
    case "models":
      return <CatalogLoading />;
    case "legal":
      return <><Heading /><SkeletonCard className="route-loading-legal-tabs"><Skeleton className="h-8 w-28" /><Skeleton className="h-8 w-28" /></SkeletonCard><SkeletonCard><ArticleLines count={10} /></SkeletonCard></>;
    case "usage":
      return <><Heading /><MetricCards /><SkeletonCard className="route-loading-chart"><Skeleton className="h-4 w-40" /><div className="route-loading-chart-bars">{[45, 70, 50, 84, 62, 92, 54, 74].map((height, index) => <Skeleton className="route-loading-chart-bar" style={{ height: `${height}%` }} key={index} />)}</div></SkeletonCard><SkeletonCard><DataRows rows={5} /></SkeletonCard></>;
    case "api":
      return <><Heading subtitle /><SkeletonCard className="route-loading-api-url"><Skeleton className="h-3 w-28" /><Skeleton className="h-4 w-56 max-w-full" /></SkeletonCard><SkeletonCard className="route-loading-api-form"><Skeleton className="h-10 flex-1" /><Skeleton className="h-10 w-36" /></SkeletonCard><SkeletonCard><Skeleton className="h-4 w-36" /><DataRows rows={4} /></SkeletonCard></>;
    case "integration-detail":
    case "guide-detail":
      return <DetailLoading />;
    case "integrations":
    case "guides":
      return <><Heading subtitle /><div className="route-loading-models">{Array.from({ length: 6 }, (_, index) => <SkeletonCard className="route-loading-model" key={index}><Skeleton className="h-9 w-9 rounded-xl" /><Skeleton className="h-4 w-2/3" /><ArticleLines count={2} /></SkeletonCard>)}</div></>;
    case "referrals":
      return <><Heading subtitle /><MetricCards count={2} /><SkeletonCard><Skeleton className="h-4 w-44" /><DataRows rows={4} /></SkeletonCard></>;
    case "support":
      return <><Heading subtitle /><div className="route-loading-models">{Array.from({ length: 2 }, (_, index) => <SkeletonCard className="route-loading-model" key={index}><Skeleton className="h-4 w-2/3" /><ArticleLines count={3} /></SkeletonCard>)}</div></>;
    case "functions":
      return <><Heading /><MetricCards count={2} /><SkeletonCard><Skeleton className="h-4 w-44" /><DataRows rows={3} /></SkeletonCard></>;
    case "profile":
      return <><Heading /><SkeletonCard><div className="route-loading-form">{Array.from({ length: 4 }, (_, index) => <Skeleton className="h-10 w-full" key={index} />)}</div></SkeletonCard></>;
    case "pricing":
      return <><Heading subtitle /><SkeletonCard><ArticleLines count={4} /></SkeletonCard><SkeletonCard className="route-loading-api-form"><Skeleton className="h-10 flex-1" /><Skeleton className="h-10 w-36" /></SkeletonCard></>;
    case "openai":
      return <DetailLoading code />;
    case "anthropic":
      return <DetailLoading code />;
    case "compare":
      return <><Heading subtitle /><SkeletonCard className="route-loading-table">{Array.from({ length: 6 }, (_, index) => <div className="route-loading-row" key={index}><Skeleton className="h-4 w-1/3" /><Skeleton className="h-4 w-1/4" /><Skeleton className="h-4 w-1/4" /></div>)}</SkeletonCard></>;
    case "studio":
      return <div className="route-loading-studio"><SkeletonCard className="route-loading-studio-history"><Skeleton className="h-4 w-28" /><DataRows rows={5} /></SkeletonCard><SkeletonCard className="route-loading-studio-chat"><Skeleton className="h-5 w-40" /><div className="route-loading-studio-message"><Skeleton className="h-8 w-8 rounded-full" /><Skeleton className="h-16 flex-1" /></div><SkeletonCard className="route-loading-studio-composer"><Skeleton className="h-16 w-full" /><Skeleton className="h-8 w-28" /></SkeletonCard></SkeletonCard></div>;
    default:
      return <><Heading /><SkeletonCard><ArticleLines count={4} /></SkeletonCard></>;
  }
}

export function SidebarLoadingFallback() {
  return (
    <aside className="app-sidebar app-sidebar-loading" aria-hidden="true" data-testid="sidebar-loading">
      <Skeleton className="sidebar-loading-brand" />
      {Array.from({ length: 7 }, (_, index) => (
        <Skeleton className="sidebar-loading-item" key={index} />
      ))}
    </aside>
  );
}

export default function RouteLoadingFallback({ pathname = "/", label }) {
  const { t } = useLang();
  const profile = getRouteLoadingProfile(pathname);
  const content = <RouteLoadingContent profile={profile} />;
  const className = `route-loading route-loading-${profile}`;

  if (profile === "docs") {
    return (
      <div className={`docs-page ${className}`} role="status" aria-busy="true" data-testid="route-loading" data-loading-profile={profile}>
        <span className="visually-hidden">{label || t("loading_hint")}</span>
        <div className="route-loading-art" aria-hidden="true">{content}</div>
      </div>
    );
  }

  if (profile === "studio") {
    return (
      <div className={`${className} route-loading-studio-page`} role="status" aria-busy="true" data-testid="route-loading" data-loading-profile={profile}>
        <span className="visually-hidden">{label || t("loading_hint")}</span>
        <div className="route-loading-art" aria-hidden="true">{content}</div>
      </div>
    );
  }

  return (
    <div className={`screen-container ${className}`} role="status" aria-busy="true" data-testid="route-loading" data-loading-profile={profile}>
      <span className="visually-hidden">{label || t("loading_hint")}</span>
      <div className="route-loading-art" aria-hidden="true">{content}</div>
    </div>
  );
}
